import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from 'react';
import { cn } from '../../lib/cn.js';
import { MonoText } from '../mono-text/MonoText.js';
const sanitize = (raw, length) => raw.replace(/\D/g, '').slice(0, length);
/**
 * OtpInput — 6-cell one-time-code input rendered over a single invisible
 * `<input autocomplete="one-time-code">` (Auth & Onboarding pattern):
 * typing auto-advances, paste fills all cells, Backspace clears backwards —
 * all native single-input behaviors; the cells are a purely visual projection
 * with the caret cell highlighted.
 */
export function OtpInput({ length = 6, value: controlledValue, defaultValue, onChange, onComplete, label, describedBy, disabled = false, invalid = false, autoFocus = false, className, ...props }) {
    const [internalValue, setInternalValue] = useState(sanitize(defaultValue ?? '', length));
    const [focused, setFocused] = useState(false);
    const value = controlledValue === undefined ? internalValue : sanitize(controlledValue, length);
    const activeIndex = Math.min(value.length, length - 1);
    const handleChange = (raw) => {
        const next = sanitize(raw, length);
        if (next === value)
            return;
        setInternalValue(next);
        onChange?.(next);
        if (next.length === length)
            onComplete?.(next);
    };
    return (_jsxs("div", { dir: "ltr", className: cn('relative inline-flex gap-2', className), ...props, children: [_jsx("input", { type: "text", inputMode: "numeric", autoComplete: "one-time-code", pattern: "[0-9]*", maxLength: length, "aria-label": label, "aria-describedby": describedBy, "aria-invalid": invalid || undefined, autoFocus: autoFocus, disabled: disabled, value: value, onChange: (event) => handleChange(event.target.value), onFocus: () => setFocused(true), onBlur: () => setFocused(false), "data-part": "otp-native-input", 
                // Invisible but interactive layer over the cells; caret hidden.
                className: "absolute inset-0 z-10 h-full w-full cursor-text opacity-0 [caret-color:transparent] disabled:cursor-default" }), Array.from({ length }, (_, index) => {
                const char = value[index] ?? '';
                const isActive = focused && !disabled && index === activeIndex && value.length < length;
                return (_jsx(MonoText, { "aria-hidden": "true", "data-part": "otp-cell", "data-active": isActive || undefined, className: cn('flex h-12 w-10 select-none items-center justify-center rounded-md border bg-surface text-[18px] font-bold text-fg', 'transition-[border-color,box-shadow] duration-100', invalid ? 'border-danger' : 'border-border-strong', isActive ? 'border-accent shadow-[0_0_0_3px_var(--accent-soft)]' : undefined, disabled ? 'opacity-40' : undefined), children: char }, index));
            })] }));
}
//# sourceMappingURL=OtpInput.js.map